import React, { useEffect, useMemo, useState } from "react";

import { CircularProgress, IconButton, TextareaAutosize, Tooltip } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";

import OutlinedSend from "../../../../assets/icons/OutlinedSend";
import { useAppState } from "../../../../context/StateContext";
import { Comment } from "../../../../models/comment";
import useStyles from "./styles";

export interface Props {
  comment?: Comment;
  isLoading?: boolean;
  maxLength?: number;
  onSubmit: (comment: Comment) => void;
  placeholderText?: string;
  hasPermission?: boolean;
}

const CommentForm = ({
  comment = {},
  isLoading = false,
  maxLength,
  onSubmit,
  placeholderText,
  hasPermission = true,
}: Props) => {
  const { currentUser } = useAppState();
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>("");

  const disabled = useMemo(() => {
    if (error) return true;
    if (isLoading) return true;
    if (message?.length <= 0) return true;

    return false;
  }, [error, message, isLoading]);

  useEffect(() => {
    if (maxLength && message?.length > maxLength) {
      setError(translate("pleaseTypeLessThanCharacters", { maxLength }));
    } else {
      setError(undefined);
    }
  }, [maxLength, message]);

  const getCurrentUserAvatar = (): string => {
    // eslint-disable-next-line global-require
    return currentUser?.picture?.mediaLink || require("Assets/profile.png");
  };

  const handleOnClickSend = () => {
    if (disabled) return;

    const now = new Date();

    onSubmit({
      ...comment,
      createdAt: comment?.createdAt || now.toISOString(),
      message,
      updatedAt: now.toISOString(),
      user: currentUser,
      userId: currentUser?.id,
    });

    setMessage("");
  };

  const onMessageKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && e.shiftKey === false) {
      e.preventDefault();
      e.stopPropagation();
      handleOnClickSend();
    }
  };

  const renderSupport = () => {
    if (error) return <p>{error}</p>;

    return (
      <>
        <p>
          <b>{translate("return")}</b> {translate("toAdd")}
        </p>
        <p>
          <b>{translate("shiftReturn")}</b> {translate("toBreakLine")}
        </p>
      </>
    );
  };

  const renderSendButton = () => {
    if (!hasPermission) {
      return null;
    }

    let content = (
      <OutlinedSend className="icon" color={disabled ? colors.gray400 : colors.white} />
    );

    if (isLoading) {
      content = <CircularProgress size={16} />;
    }

    return (
      <IconButton className={classes.sendButton} disabled={disabled} onClick={handleOnClickSend}>
        {content}
      </IconButton>
    );
  };

  return (
    <div className={classes.content}>
      <Tooltip title={currentUser?.fullName || ""}>
        <img className={classes.userAvatar} src={getCurrentUserAvatar()} />
      </Tooltip>
      <div className={classes.form}>
        <div className={`${classes.formField} ${error ? classes.formFieldError : ""}`}>
          <div className={classes.commentContent}>
            <TextareaAutosize
              className={classes.commentInput}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={onMessageKeyDown}
              placeholder={placeholderText}
              value={message}
              disabled={isLoading || !hasPermission}
            />
          </div>
          {renderSendButton()}
        </div>
        <div className={`${classes.supportContent} ${error ? classes.supportContentError : ""}`}>
          <div className={classes.supportContentLeft}>{renderSupport()}</div>
          {maxLength && (
            <div className={classes.supportContentRight}>
              {message.length} / {maxLength}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentForm;
